import React, { useState } from "react";
import { Sword, Heart, Shield, Sparkles, Users, Target, ChevronDown, Check } from "lucide-react";

const SLOT_COUNT = 5; // standard CCG battlefield slot count

function AllyTile({ ally, registerRef, targets, currentTargetKey, onPickTarget, isIt, alliesAttacking }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const hpPct = Math.max(0, Math.min(100, (ally.hp / Math.max(1, ally.max_hp)) * 100));
  const hpColor = hpPct > 60 ? "bg-emerald-500" : hpPct > 30 ? "bg-amber-500" : "bg-red-500";
  const bond = ally.bond_level || 0;
  const hasChoice = (targets || []).length > 1;
  const currentTarget = (targets || []).find((t) => t.key === currentTargetKey) || (targets || [])[0];

  return (
    <div
      ref={(el) => registerRef && registerRef(`ally-field-${ally.id}`, el)}
      data-testid={`battlefield-ally-${ally.id}`}
      className={`relative ccg-card-slot ccg-card-slot-player flex flex-col justify-between shrink-0 overflow-hidden bg-gradient-to-b from-[#102d18] via-[#091b10] to-[#040e08] border-2 border-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.35)] hover:scale-105 transition-all duration-300 z-10 ${alliesAttacking ? "anim-ally-lunge" : ""}`}
    >
      {/* Portrait */}
      <div className="relative h-[56px] sm:h-[66px] bg-black/40 overflow-hidden border-b border-emerald-900/60 w-full shrink-0">
        {ally.image_url ? (
          <img
            src={ally.image_url}
            alt={ally.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Users size={28} className="text-emerald-700/60" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent pointer-events-none" />
        {bond > 1 && (
          <div
            className="absolute top-0.5 right-0.5 px-1 py-0.5 rounded bg-black/80 border border-[#cfb53b]/50 flex items-center gap-0.5 z-10"
            title={`Bond Lv.${bond}`}
          >
            <Sparkles size={8} className="text-[#cfb53b]" />
            <span className="text-[7px] font-heading text-[#cfb53b]">Lv.{bond}</span>
          </div>
        )}
      </div>

      {/* Name */}
      <div className="px-1 py-0.5 text-center shrink-0 w-full">
        <p className="font-heading text-[9px] sm:text-[10px] text-white leading-tight truncate">
          {ally.name}
        </p>
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between px-1.5 py-0.5 text-[8px] sm:text-[9px] font-heading shrink-0 w-full bg-black/20">
        <span className="flex items-center gap-0.5 text-red-300">
          <Sword size={8} /> {ally.attack ?? 0}
        </span>
        <span className="flex items-center gap-0.5 text-blue-300">
          <Shield size={8} /> {ally.defense ?? 0}
        </span>
      </div>

      {/* HP bar */}
      <div className="px-1.5 py-0.5 shrink-0 w-full">
        <div className="h-1 rounded-full bg-black/60 overflow-hidden border border-black/40">
          <div className={`h-full ${hpColor} transition-all duration-500`} style={{ width: `${hpPct}%` }} />
        </div>
        <div className="flex items-center justify-center gap-0.5 mt-0.5 text-[7px] text-pink-200">
          <Heart size={7} fill="currentColor" /> {ally.hp}/{ally.max_hp}
        </div>
      </div>

      {/* Target picker */}
      {hasChoice ? (
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          data-testid={`ally-target-btn-${ally.id}`}
          className="w-full flex items-center justify-center gap-0.5 px-1 py-1 bg-black/70 hover:bg-black/90 border-t border-emerald-900/60 text-[7px] font-heading text-emerald-200 transition-colors shrink-0"
          title={isIt ? "Cambia bersaglio" : "Change target"}
        >
          <Target size={8} className="text-amber-300 shrink-0" />
          <span className="truncate max-w-[50px]">{currentTarget?.label || "?"}</span>
          <ChevronDown size={7} className={menuOpen ? "rotate-180 transition-transform shrink-0" : "transition-transform shrink-0"} />
        </button>
      ) : (
        <div className="w-full flex items-center justify-center gap-0.5 px-1 py-1 bg-black/50 border-t border-emerald-900/60 text-[7px] text-emerald-300/60 shrink-0" title={currentTarget?.label}>
          <Target size={8} className="shrink-0" />
          <span className="truncate max-w-[60px]">{currentTarget?.label || "—"}</span>
        </div>
      )}

      {/* Target dropdown */}
      {menuOpen && hasChoice && (
        <div
          data-testid={`ally-target-menu-${ally.id}`}
          className="absolute left-1 right-1 bottom-8 bg-[#0a0a0c]/95 border border-emerald-500/50 rounded shadow-2xl z-30 overflow-hidden"
        >
          {targets.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => { onPickTarget(ally.id, t.key); setMenuOpen(false); }}
              data-testid={`ally-target-${ally.id}-${t.key}`}
              className={`w-full text-left px-1.5 py-1 text-[8px] font-heading transition-colors ${
                t.key === currentTargetKey
                  ? "bg-emerald-950 text-emerald-200"
                  : "hover:bg-emerald-950/50 text-white/80"
              }`}
            >
              <span className="flex items-center gap-1">
                {t.key === currentTargetKey && <Check size={7} className="text-emerald-300 shrink-0" />}
                <span className="truncate">{t.label}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptySlot() {
  return (
    <div
      aria-hidden
      className="ccg-card-slot ccg-card-slot-player shrink-0 opacity-40"
    >
      <Users size={20} className="text-emerald-800/30" />
    </div>
  );
}

export default function BattleAlliedField({ allies, lang, registerRef, enemies, allyTargets, onPickTarget, alliesAttacking }) {
  const list = allies || [];
  const isIt = (lang || "en").startsWith("it");
  const placeholderCount = Math.max(0, SLOT_COUNT - list.length);

  const targets = React.useMemo(() => {
    const opts = [{
      key: "enemy",
      label: enemies?.main?.name || (isIt ? "Nemico" : "Enemy"),
    }];
    (enemies?.minions || []).forEach((m, i) => {
      if ((m.hp ?? 0) > 0) {
        opts.push({ key: `minion-${i}`, label: m.name || `Minion ${i + 1}` });
      }
    });
    return opts;
  }, [enemies, isIt]);

  return (
    <div
      data-testid="battlefield-allies"
      className="w-full flex justify-center py-2"
    >
      <div className="ccg-slot-row overflow-x-auto no-scrollbar">
        {list.map((ally, i) => (
          <AllyTile
            key={`${ally.id}-${i}`}
            ally={ally}
            registerRef={registerRef}
            targets={targets}
            currentTargetKey={(allyTargets && allyTargets[ally.id]) || "enemy"}
            onPickTarget={onPickTarget}
            isIt={isIt}
            alliesAttacking={alliesAttacking}
          />
        ))}
        {Array.from({ length: placeholderCount }).map((_, i) => (
          <EmptySlot key={`empty-${i}`} />
        ))}
      </div>
    </div>
  );
}
